import { Wallet, RotateCcw } from "lucide-react";
import { useWallet } from "@/contexts/WalletContext";
import { fmtUSD, fmtSPURR } from "./fmt";

function WalletHint() {
  const { anyConnected, primaryAddress, short, connectPhantom } = useWallet();
  return (
    <div
      className="mt-3 pt-3 border-t border-[#3a3a3a] flex items-center justify-between gap-3"
      data-testid="trade-wallet-hint"
    >
      {anyConnected ? (
        <>
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#00ff41]">
            <span className="w-1.5 h-1.5 bg-[#00ff41] live-dot" />
            Connected · {short(primaryAddress)}
          </div>
          <a
            href="#wallet"
            data-testid="trade-go-mainnet"
            className="font-mono text-[11px] uppercase tracking-widest text-[#cfff04] hover:underline"
          >
            Mint for real ↗
          </a>
        </>
      ) : (
        <button
          onClick={connectPhantom}
          data-testid="trade-connect-wallet"
          className="w-full flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#e2e2e2] hover:text-[#cfff04] py-1"
        >
          <Wallet size={11} /> Connect wallet to deploy for real
        </button>
      )}
    </div>
  );
}

export default function TradeTerminal({
  side, setSide, amount, setAmount, price, busy, onSubmit, wallet, portfolio, onReset,
}) {
  return (
    <div className="col-span-12 lg:col-span-4 border border-[#3a3a3a] bg-[#121212]" data-testid="trade-panel">
      <div className="flex border-b border-[#3a3a3a]">
        <button
          onClick={() => setSide("buy")}
          data-testid="side-buy"
          className={`flex-1 py-4 font-display font-bold uppercase tracking-wider text-sm transition-colors ${
            side === "buy" ? "bg-[#00ff41] text-[#050505]" : "text-[#e2e2e2] hover:text-white"
          }`}
        >
          Buy
        </button>
        <button
          onClick={() => setSide("sell")}
          data-testid="side-sell"
          className={`flex-1 py-4 font-display font-bold uppercase tracking-wider text-sm transition-colors ${
            side === "sell" ? "bg-[#ff3333] text-white" : "text-[#e2e2e2] hover:text-white"
          }`}
        >
          Sell
        </button>
      </div>

      <div className="p-5 space-y-4">
        <div>
          <label className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0] block mb-2">
            Amount (USD)
          </label>
          <input
            data-testid="trade-amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full bg-[#0c0c0c] border border-[#3a3a3a] focus:border-[#cfff04] focus:outline-none px-4 py-3 font-mono text-lg"
            placeholder="0.00"
            min="1"
          />
          <div className="flex gap-2 mt-2">
            {[25, 50, 100, 500].map((v) => (
              <button
                key={v}
                onClick={() => setAmount(String(v))}
                data-testid={`quick-${v}`}
                className="flex-1 border border-[#3a3a3a] hover:border-[#cfff04] py-1 font-mono text-[11px] text-[#e2e2e2] hover:text-[#cfff04]"
              >
                ${v}
              </button>
            ))}
          </div>
        </div>

        <div className="border border-[#3a3a3a] bg-[#050505] p-3 space-y-2 font-mono text-xs">
          <div className="flex justify-between">
            <span className="text-[#d0d0d0] uppercase tracking-wider">You {side === "buy" ? "pay" : "receive"}</span>
            <span>${parseFloat(amount || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#d0d0d0] uppercase tracking-wider">You {side === "buy" ? "receive" : "pay"}</span>
            <span className="text-[#cfff04]">
              {price ? fmtSPURR(parseFloat(amount || 0) / price.price) : "—"} SPURR
            </span>
          </div>
          <div className="flex justify-between pt-2 border-t border-[#333]">
            <span className="text-[#d0d0d0] uppercase tracking-wider">Price</span>
            <span>${price?.price?.toFixed(8) || "—"}</span>
          </div>
        </div>

        <button
          onClick={onSubmit}
          disabled={busy}
          data-testid="trade-submit"
          className={`w-full py-4 font-display font-bold uppercase tracking-wider text-sm btn-brutal ${
            side === "buy" ? "bg-[#00ff41] text-[#050505]" : "bg-[#ff3333] text-white"
          } ${busy ? "opacity-50" : ""}`}
        >
          {busy ? "Submitting..." : `${side === "buy" ? "Buy" : "Sell"} SPURR`}
        </button>

        {/* Wallet */}
        <div className="pt-4 border-t border-[#3a3a3a] grid grid-cols-2 gap-3 font-mono text-xs" data-testid="wallet-balances">
          <div>
            <div className="text-[#d0d0d0] uppercase tracking-wider">USD</div>
            <div className="text-white text-lg mt-1">{fmtUSD(wallet?.usd_balance)}</div>
          </div>
          <div>
            <div className="text-[#d0d0d0] uppercase tracking-wider">SPURR</div>
            <div className="text-[#cfff04] text-lg mt-1">{fmtSPURR(wallet?.emg_balance)}</div>
          </div>
          <div className="col-span-2 pt-3 border-t border-[#333]">
            <div className="text-[#d0d0d0] uppercase tracking-wider">Portfolio Value</div>
            <div className="text-white text-2xl font-bold mt-1">{fmtUSD(portfolio)}</div>
          </div>
        </div>

        <button
          onClick={onReset}
          data-testid="wallet-reset"
          className="w-full mt-2 border border-[#3a3a3a] hover:border-[#ff3333] text-[#d0d0d0] hover:text-[#ff3333] py-2 font-mono text-[11px] uppercase tracking-widest flex items-center justify-center gap-2"
        >
          <RotateCcw size={11} /> Reset paper wallet
        </button>

        <WalletHint />
      </div>
    </div>
  );
}
